/**
 * Notification Types
 * Based on the new backend GraphQL myNotifications query
 */

// Channel types for notifications
export type NotificationChannel = 'push' | 'sms' | 'email' | 'in_app'

// Status types for notifications
export type NotificationStatus = 'pending' | 'sent' | 'delivered' | 'failed' | 'read'

// Visual type for UI display (derived from status/channel)
export type NotificationType = 'info' | 'success' | 'warning' | 'error'

/**
 * Raw notification log from GraphQL API
 */
export interface NotificationLogRaw {
  id: number // Note: backend uses integer id, not UUID
  title: string
  body: string
  channel: NotificationChannel
  status: NotificationStatus
  isRead: boolean
  readAt: string | null
  timestamp: string
}

/**
 * GraphQL response structure for myNotifications query
 */
export interface MyNotificationsResponse {
  myNotifications: {
    totalCount: number
    logs: NotificationLogRaw[]
  }
}

/**
 * Transformed notification for UI consumption
 */
export interface Notification {
  id: number
  title: string
  body: string
  channel: NotificationChannel
  status: NotificationStatus
  type: NotificationType // Derived field for UI styling
  isRead: boolean
  readAt: string | null
  timestamp: string
  createdAt: Date // Parsed timestamp for easier date operations
}

/**
 * Pagination options for fetching notifications
 */
export interface NotificationQueryOptions {
  limit?: number
  offset?: number
  isRead?: boolean
}

/**
 * Dismiss all notifications request body
 */
export interface DismissAllOptions {
  profile_type?: 'provider' | 'client' | 'organization'
  channel?: NotificationChannel
}

/**
 * FCM Device registration payload
 */
export interface FCMDeviceRegistration {
  registration_id: string
  type: 'web' | 'android' | 'ios'
  device_id?: string
  name?: string
}

/**
 * Notification context state
 */
export interface NotificationState {
  notifications: Notification[]
  unreadCount: number
  totalCount: number
  hasUnreadNotifications: boolean
  loading: boolean
  error: Error | null
}

/**
 * Notification context actions
 */
export interface NotificationActions {
  markAsRead: (id: number) => Promise<void>
  markAllAsRead: (options?: DismissAllOptions) => Promise<void>
  refetch: () => void
  setPollingEnabled: (enabled: boolean) => void
}

/**
 * Helper function to derive notification type from status
 */
export function getNotificationTypeFromStatus(status: NotificationStatus): NotificationType {
  switch (status) {
    case 'delivered':
    case 'read':
      return 'success'
    case 'pending':
    case 'sent':
      return 'info'
    case 'failed':
      return 'error'
    default:
      return 'info'
  }
}

/**
 * Transform raw notification log to UI notification
 */
export function transformNotification(raw: NotificationLogRaw): Notification {
  return {
    id: raw.id,
    title: raw.title,
    body: raw.body,
    channel: raw.channel,
    status: raw.status,
    type: getNotificationTypeFromStatus(raw.status),
    isRead: raw.isRead,
    readAt: raw.readAt,
    timestamp: raw.timestamp,
    createdAt: new Date(raw.timestamp),
  }
}
